/**
 * Environment access — v.1-D §7.1.
 *
 * Vite exposes user-facing variables under `import.meta.env` at build time.
 * `readRequired` throws when a required value is missing so the failure is
 * loud and immediate; `readOptional` falls back to a documented default.
 *
 * The COOKIE_DOMAIN default permits local-dev sign-in on `localhost` without
 * breaking production cross-domain cookies (see §7.4, Decision D-017).
 *
 * Ported verbatim by T304 from `frontend-v2/src/lib/env.ts` — FR-002 fail-loud
 * env contract is the load-bearing v.3 invariant: a missing `VITE_CONVEX_URL`
 * must throw at module load, not fall back silently. The T303 foundation
 * test (`tests/foundation/env.test.ts`) pins both the throw shape and the
 * `.env.example` instruction text in the error message.
 */
function readRequired(name: string): string {
  const value = import.meta.env[name];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `Environment variable ${name} is not set. Copy .env.example to .env.local and fill in the value.`,
    );
  }
  return value;
}

function readOptional(name: string, fallback: string): string {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export const env = {
  CONVEX_URL: readRequired('VITE_CONVEX_URL'),
  COOKIE_DOMAIN: readOptional('VITE_COOKIE_DOMAIN', '.mediatiquepress.one'),
  LOG_LEVEL: readOptional(
    'VITE_LOG_LEVEL',
    import.meta.env.DEV ? 'debug' : 'info',
  ),
  IS_DEV: import.meta.env.DEV,

  // NMW-1239 — Auth0 console sign-in. REQUIRED, like VITE_CONVEX_URL: Auth0 is
  // the deployment's only issuer (convex/auth.config.ts), so a build without
  // these cannot log anyone in. Failing at module load is the loud version of
  // that; the quiet version is a sign-in button that redirects to
  // `https://undefined/authorize`.
  //
  // Neither is a secret — both travel in every login URL — but both are
  // per-environment, and they come from that environment's `infra/auth0`
  // outputs (`tofu output -raw domain` / `-raw client_id`).
  //
  // The domain is the BARE host; `@convex-dev` and `@auth0/auth0-react` add
  // the scheme, and convex/auth.config.ts does the same, so the env files and
  // the Convex deployments hold the identical string. There is no audience:
  // Convex validates the ID token, whose `aud` is the client id.
  AUTH0_DOMAIN: readRequired('VITE_AUTH0_DOMAIN'),
  AUTH0_CLIENT_ID: readRequired('VITE_AUTH0_CLIENT_ID'),
};
